import {
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ControlModeTemplateFieldsType } from '@src/types/ControlModes';
import {
  CombinedControlModeField,
  IStringInputFieldControlMode,
} from '@src/types/Stacks';
import React from 'react';

interface FieldEditorProps {
  field: CombinedControlModeField;
  onChange: (value: string | number | boolean | null) => void;
  errorKey: string;
  validationErrors: Record<string, string>;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  onChange,
  errorKey,
  validationErrors,
}) => {
  const error = validationErrors[errorKey];

  switch (field.type) {
    case ControlModeTemplateFieldsType.STRING:
      return (
        <TextField
          label={`${field.uiLabel} (${field.type})`}
          value={field.value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          margin='dense'
          error={!!error}
          helperText={error}
        />
      );
    case ControlModeTemplateFieldsType.NUMBER:
      return (
        <TextField
          label={`${field.uiLabel} (${field.type})`}
          type='number'
          value={field.value}
          onChange={(e) => onChange(Number(e.target.value))}
          fullWidth
          margin='dense'
          error={!!error}
          helperText={error}
          InputProps={{
            inputProps:
              'range' in field && field.range
                ? {
                    min: field.range.min,
                    max: field.range.max,
                  }
                : {},
          }}
        />
      );
    case ControlModeTemplateFieldsType.BOOLEAN:
      return (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(field.value)}
                onChange={(e) => onChange(e.target.checked)}
              />
            }
            label={`${field.uiLabel} (${field.type})`}
          />
          {error && (
            <Typography color='error' variant='caption'>
              {error}
            </Typography>
          )}
        </Box>
      );
    case ControlModeTemplateFieldsType.SELECT:
      return (
        <TextField
          select
          label={`${field.uiLabel} (${field.type})`}
          value={field.value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          margin='dense'
          error={!!error}
          helperText={error}
        >
          {'listOptions' in field && field.listOptions
            ? field.listOptions.map((option: string) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))
            : null}
        </TextField>
      );
    default: {
      const defaultField = field as IStringInputFieldControlMode;
      return (
        <TextField
          label={`${defaultField.uiLabel} (${defaultField.type})`}
          value={defaultField.value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          margin='dense'
          error={!!error}
          helperText={error}
        />
      );
    }
  }
};

export default React.memo(FieldEditor);
